<script setup>
// NOTICE: KAPTION-INTEGRATION
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { useRoute } from 'vue-router';
import { useSidebarContext } from './provider';
import Icon from 'next/icon/Icon.vue';
// NOTICE: KAPTION-INTEGRATION - Favorites
import { useKaptionFeatureFlags } from 'dashboard/composables/useKaptionFeatureFlags';
import { useKaptionFavorites } from 'dashboard/composables/useKaptionFavorites';
import { useI18n } from 'vue-i18n';
import { useAdmin } from 'dashboard/composables/useAdmin';

const props = defineProps({
  children: { type: Array, required: true },
  label: { type: String, required: true },
  triggerElement: { type: Object, default: null },
});

const emit = defineEmits(['close']);

// NOTICE: KAPTION-INTEGRATION - Favorites
const { isKaptionFlagEnabled } = useKaptionFeatureFlags();
const {
  isFavorite,
  isAccountFavorite,
  isPersonalFavorite,
  togglePersonalFavorite,
  toggleAccountFavorite,
} = useKaptionFavorites();
const { t } = useI18n();
const { isAdmin } = useAdmin();

// NOTICE: KAPTION-INTEGRATION - Check if starring feature is enabled
const starringEnabled = computed(() =>
  isKaptionFlagEnabled('KAPTION_SIDEBAR_STARRING')
);

// NOTICE: KAPTION-INTEGRATION - Check if a child can be starred
const canStarChild = child => {
  return starringEnabled.value && child.starType && child.starId != null;
};

const isChildFavorite = child => {
  if (!canStarChild(child)) return false;
  return isFavorite(child.starType, child.starId);
};

const isChildAccountFavorite = child => {
  if (!canStarChild(child)) return false;
  return isAccountFavorite(child.starType, child.starId);
};

const isChildPersonalFavorite = child => {
  if (!canStarChild(child)) return false;
  return isPersonalFavorite(child.starType, child.starId);
};

// NOTICE: KAPTION-INTEGRATION - Toggle favorite for a child
const handleStarClick = async (event, child) => {
  event.preventDefault();
  event.stopPropagation();
  if (!canStarChild(child)) return;

  // If this is an account favorite, non-admins can't change it.
  if (isChildAccountFavorite(child) && !isAdmin.value) return;

  // Admins can toggle account favorites with Shift-click.
  if (isAdmin.value && event.shiftKey) {
    await toggleAccountFavorite(child.starType, child.starId);
    return;
  }

  togglePersonalFavorite(child.starType, child.starId);
};

// NOTICE: KAPTION-INTEGRATION - Get tooltip
const getStarTooltip = child => {
  if (isChildAccountFavorite(child) && !isChildPersonalFavorite(child)) {
    return isAdmin.value
      ? t('SIDEBAR.ACCOUNT_FAVORITE_ADMIN_HINT')
      : t('SIDEBAR.ACCOUNT_FAVORITE');
  }
  return isChildFavorite(child)
    ? t('SIDEBAR.UNSTAR_ITEM')
    : t('SIDEBAR.STAR_ITEM');
};

const route = useRoute();
const { isAllowed, resolvePath } = useSidebarContext();
const dropdownRef = ref(null);

// Group children by their parent group
const groupedChildren = computed(() => {
  const groups = [];
  const directChildren = [];

  props.children.forEach(child => {
    if (child.children) {
      const accessibleChildren = child.children.filter(
        subChild => subChild.to && isAllowed(subChild.to)
      );
      if (accessibleChildren.length > 0) {
        groups.push({
          label: child.label,
          icon: child.icon,
          children: accessibleChildren,
        });
      }
    } else if (child.to && isAllowed(child.to)) {
      directChildren.push(child);
    }
  });

  // Direct children come first, then grouped children
  const result = [];
  if (directChildren.length > 0) {
    result.push({ label: null, children: directChildren });
  }
  result.push(...groups);

  return result;
});

// NOTICE: KAPTION-INTEGRATION
// Enhanced isActive to handle query param-based navigation (like AI Studio sections)
const isActive = child => {
  if (!child.to) return false;

  const childPath = resolvePath(child.to);

  // First check activeOn if available
  if (child.activeOn?.includes(route.name)) return true;

  // Check if paths match
  if (route.path !== childPath) return false;

  // Paths match - now check if we need to compare query params
  // If the child.to has query params, compare them with current route
  const childQuery = child.to?.query || {};
  const hasChildQuery = Object.keys(childQuery).length > 0;

  if (hasChildQuery) {
    // Compare relevant query params
    return Object.entries(childQuery).every(
      ([key, value]) => route.query[key] === value
    );
  }

  // No query params to compare, path match is enough
  // But only if current route also has no relevant query params
  const currentQuery = route.query || {};
  const hasCurrentQuery = Object.keys(currentQuery).length > 0;

  // If current route has query params but child doesn't, don't match
  // (another child with matching query should be active instead)
  return !hasCurrentQuery;
};

const handleClickOutside = event => {
  if (dropdownRef.value && !dropdownRef.value.contains(event.target)) {
    // Check if the click is on the trigger element
    if (props.triggerElement && props.triggerElement.contains(event.target)) {
      return;
    }
    emit('close');
  }
};

onMounted(() => {
  document.addEventListener('click', handleClickOutside);
});

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside);
});

const handleItemClick = () => {
  emit('close');
};
</script>

<template>
  <Teleport to="body">
    <div
      ref="dropdownRef"
      class="fixed top-[var(--dropdown-top)] left-[var(--dropdown-left)] z-[9999] w-64 bg-n-solid-2 border border-n-weak rounded-3xl shadow-lg max-h-[80vh] overflow-y-auto no-scrollbar"
      :style="{
        '--dropdown-top':
          (triggerElement?.getBoundingClientRect()?.top || 0) + 'px',
        '--dropdown-left':
          (triggerElement?.getBoundingClientRect()?.right || 0) + 8 + 'px',
      }"
    >
      <div class="p-2">
        <div
          class="px-2 py-1.5 text-xs font-semibold text-n-slate-11 uppercase"
        >
          {{ label }}
        </div>
        <div
          v-for="(group, groupIndex) in groupedChildren"
          :key="groupIndex"
          class="mt-1"
        >
          <div
            v-if="group.label"
            class="flex items-center gap-2 px-2 py-1 text-sm font-medium text-n-slate-10"
          >
            <Icon v-if="group.icon" :icon="group.icon" class="size-3" />
            <span>{{ group.label }}</span>
          </div>
          <!-- NOTICE: KAPTION-INTEGRATION - Wrapper for star button -->
          <div
            v-for="child in group.children"
            :key="child.name"
            class="flex items-center gap-1 group/star"
          >
            <RouterLink
              :to="child.to"
              class="flex items-center gap-2 px-2 py-1.5 rounded-2xl text-sm hover:bg-n-alpha-2 min-w-0 flex-1"
              :class="{
                'text-n-blue-text bg-n-alpha-2 font-medium': isActive(child),
                'text-n-slate-11': !isActive(child),
              }"
              @click="handleItemClick"
            >
              <Icon
                v-if="child.icon"
                :icon="child.icon"
                class="size-4 flex-shrink-0"
              />
              <span class="truncate flex-1">{{ child.label }}</span>
            </RouterLink>
            <!-- NOTICE: KAPTION-INTEGRATION - Star toggle button -->
            <button
              v-if="canStarChild(child)"
              v-tooltip.top="getStarTooltip(child)"
              type="button"
              class="flex-shrink-0 size-6 p-0 flex items-center justify-center rounded transition-opacity hover:bg-n-alpha-2"
              :class="{
                'opacity-100 text-yellow-500': isChildPersonalFavorite(child),
                'opacity-100 text-n-blue-text':
                  isChildAccountFavorite(child) &&
                  !isChildPersonalFavorite(child),
                'opacity-0 group-hover/star:opacity-100 text-n-slate-10 hover:text-yellow-500':
                  !isChildFavorite(child),
              }"
              @click="handleStarClick($event, child)"
            >
              <span
                class="size-3.5 i-lucide-star"
                :style="isChildFavorite(child) ? 'fill: currentColor' : ''"
              />
            </button>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>
